import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { identifyUser } from '../../../../lib/analytics/identifyUser';
import Breadcrumbs from '../../../../components/Breadcrumbs';
import CheckoutContext from '../../../../lib/CheckoutContext';
import * as events from '../../../../lib/analytics/events';
import { expectUnreachable } from '../../../../lib/util';
import CheckoutSection from '../../../../models/CheckoutSection';
import styles from './StepContainer.module.scss';
import { getFiGrowthBook } from '../../../../lib/growthbook';
import classNames from 'classnames';
import useGiftCart from '../../../../hooks/useGiftCart';

function titleForSection(
  checkoutSection: CheckoutSection,
  requiresShippingDetails: boolean,
  isAllGifts: boolean,
  t: TFunction,
) {
  if (checkoutSection === CheckoutSection.shipping) {
    return isAllGifts ? 'Gift Delivery' : requiresShippingDetails ? t('shipping') : t('account');
  } else if (checkoutSection === CheckoutSection.payment) {
    return t('payment');
  } else if (checkoutSection === CheckoutSection.confirm) {
    return t('review');
  } else if (checkoutSection === CheckoutSection.thankYou) {
    return t('thank_you');
  } else {
    expectUnreachable(checkoutSection);
  }
}

interface StepContainerProps {
  checkoutSection: CheckoutSection;
  children: React.ReactNode;
}

export default function StepContainer({ checkoutSection, children }: StepContainerProps) {
  const { t } = useTranslation();
  const { isAllGifts } = useGiftCart();
  const { checkoutState, requiresShippingDetails, session } = useContext(CheckoutContext);
  const showCartTest =
    checkoutSection === CheckoutSection.confirm && getFiGrowthBook().getFeatureValue('ecom-cart-test', false);

  // When the user proceeds to a new section, log that impression
  useEffect(() => {
    switch (checkoutSection) {
      case CheckoutSection.confirm:
        events.confirmation.viewed();
        break;
      case CheckoutSection.payment:
        events.payment.viewed();
        break;
      case CheckoutSection.shipping:
        events.shipping.viewed();
        break;
      default:
    }
  }, [checkoutSection]);

  // Update user identity information as it is filled in
  useEffect(() => {
    if (session?.userId) {
      const fullName = [checkoutState.customerName?.firstName, checkoutState.customerName?.lastName]
        .filter((name) => !!name)
        .join(' ');

      identifyUser(session.userId, {
        email: session.email,
        name: { fullName },
        billingInfo: checkoutState.billingInfo,
        shippingAddress: checkoutState.shippingAddress,
      });
    }
  }, [session, checkoutState.customerName, checkoutState.billingInfo, checkoutState.shippingAddress]);

  return (
    <>
      <div className={styles.mobileBreadcrumbs}>
        <Breadcrumbs />
      </div>
      <div className={classNames(styles.container, { 'ecom-cart-test': showCartTest })}>
        <div className={styles.titleColumn}>
          <h2>{titleForSection(checkoutSection, requiresShippingDetails, isAllGifts, t)}</h2>
        </div>
        {children}
      </div>
    </>
  );
}
